import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import { LocationSection } from './ClockIn'

// services
import { useMutation, useQueryClient } from 'react-query'
import { calculateTimeDifferenceFormatted, formatTimeToAmPm } from 'services/helper/DateTime'
import { postClockOut } from 'services/api/time-attendance'
import MapImage from '../MapImage'
import { format } from 'date-fns'

const ClockOut = ({ props }) => {
  const formattedClockIn = formatTimeToAmPm(props?.data?.clockin?.clockin)
  const stamped = props?.stampedClockOutTime || new Date()
  const clockOutTimeString = format(stamped, 'HH:mm:ss')

  const formattedClockOut = formatTimeToAmPm(clockOutTimeString)
  const totalTime = calculateTimeDifferenceFormatted(
    props?.data?.clockin?.clockin,
    clockOutTimeString
  )

  const [notes, setNotes] = useState(null)

  const handleNoteChange = (e) => {
    setNotes(e.target.value)
  }

  const queryClient = useQueryClient()

  const isAddressPending = (() => {

    if (props?.permission !== 'granted') return false

    const hasLatLon =
      props?.coordinates?.latitude != null &&
      props?.coordinates?.longitude != null

    if (!hasLatLon) return true

    if (props?.locationIsLoading) return true

    if (!props?.locationData && !props?.locationIsError) return true

    return false
  })()


  const closeDrawer = () => {
    if (typeof props?.closeDrawer === 'function') {
      props.closeDrawer()
      return
    }

    // fallback (ClockInDrawer passes this)
    if (typeof props?.setDrawerOpen === 'function') {
      props.setDrawerOpen(false)
    }
  }

  const resetFlow = () => {
    props?.setPage?.(1)
    props?.setActive?.(false)
  }

  const { mutate: mutateClockOut, isLoading: isClockOutLoading } = useMutation(
    (payload) => postClockOut(payload),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(['getAttendanceDashboard'], { exact: false })
        props?.setSuccessSnackbarOpen?.('Clock-out successful')

        resetFlow()
        closeDrawer()
      },
      onError: (error) => {
        props?.setErrorSnackbarOpen?.(error?.response?.data?.message || 'Failed to clock out')
      },
    }
  )

  const handleClockOut = () => {
    const attendanceId = props?.data?.id
    const modeId = props?.data?.clockin?.mode_id ?? props?.data?.mode_id

    if (!attendanceId || !modeId) {
      props?.setErrorSnackbarOpen?.('Missing attendance_id / mode_id')
      return
    }

    const lat =
      props?.coordinates?.latitude !== undefined && props?.coordinates?.latitude !== null
        ? String(props.coordinates.latitude)
        : null

    const lon =
      props?.coordinates?.longitude !== undefined && props?.coordinates?.longitude !== null
        ? String(props.coordinates.longitude)
        : null

    mutateClockOut({
      attendance_id: attendanceId,
      mode_id: modeId,
      clockout: clockOutTimeString,
      latitude: lat,
      longitude: lon,
      remarks: notes,
      street: props?.locationData?.formatted_address || props?.locationData?.display_name,
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h3" sx={{ paddingY: '20px', textAlign: 'center' }}>
        Confirm clock out?
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: 1,
          borderColor: 'gray-line.main',
          borderRadius: '4.5px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            padding: '16px',
            borderBottom: 1,
            borderBottomColor: 'gray-line.main',
          }}
        >
          <Typography sx={{ color: 'light-gray-text.main' }}>Today's total</Typography>
          <Typography variant="h4">{`${totalTime} hrs`}</Typography>
        </Box>

        <Box sx={{ display: 'flex' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              padding: '16px',
              width: '100%',
              borderRight: 1,
              borderRightColor: 'gray-line.main',
            }}
          >
            <Typography sx={{ color: 'light-gray-text.main' }}>Clock in</Typography>
            <Typography variant="h4">{formattedClockIn}</Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              padding: '16px',
              width: '100%',
            }}
          >
            <Typography sx={{ color: 'light-gray-text.main' }}>Clock out</Typography>
            <Typography variant="h4">{formattedClockOut}</Typography>
          </Box>
        </Box>
      </Box>

      <Box>
        <LocationSection
          type="clockOut"
          getUserLocation={props?.getUserLocation}
          locationData={props?.locationData}
          permission={props?.permission}
          handleNoteChange={handleNoteChange}
          setPage={props?.setPage}
          coordinates={props?.coordinates}
          locationLoading={props?.locationLoading}
          geocodingLoading={props?.locationIsLoading}
          geocodingError={props?.locationIsError}
        />

        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            padding: '24px',
            marginTop: 'auto',
            marginX: '-24px',
            marginBottom: '-16px',
          }}
        >
          <AdxButton
            variant="outlined"
            sx={{ width: '100%' }}
            onClick={() => props?.setPage?.(1)}
            disabled={isClockOutLoading}
          >
            Cancel
          </AdxButton>

          <AdxButton
            variant="contained"
            sx={{ width: '100%' }}
            onClick={handleClockOut}
            disabled={isClockOutLoading || isAddressPending}
          >
            {isAddressPending ? 'Fetching address…' : 'Clock out'}
          </AdxButton>
        </Box>
      </Box>
    </Box>
  )
}

export default ClockOut
